import React, { useLayoutEffect, useRef, useState } from 'react'
import type { PropsWithChildren } from 'react'

import { useWebinarColors } from '../../Designs/blocs/useWebinarColors'

import './chyron.scss'

export enum ChyronSize {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
  Fullscreen = 'fullscreen',
  Dynamic = 'dynamic',
}

type ChyronProps = PropsWithChildren<
  {
    size: ChyronSize
  } & (
    | {
        kicker: string
        kickerPosition?: 'top'
      }
    | {
        kicker?: undefined
        kickerPosition: 'left'
      }
  )
>

const Chyron = ({
  size,
  children,
  kicker,
  kickerPosition = 'top',
}: ChyronProps) => {
  const webinarColors = useWebinarColors()

  const chyronRef = useRef<HTMLDivElement>(null)
  const [actualSize, setActualSize] = useState(
    size === ChyronSize.Dynamic ? ChyronSize.Small : size
  )

  useLayoutEffect(() => {
    if (size === ChyronSize.Dynamic) {
      setActualSize(ChyronSize.Small)
    }
  }, [children, size])

  useLayoutEffect(() => {
    if (size === ChyronSize.Dynamic && chyronRef.current) {
      const elementsToCheck: Element[] = [chyronRef.current]
      chyronRef.current
        .querySelectorAll('.dynamic-section')
        .forEach((e) => elementsToCheck.push(e))

      elementsToCheck.some((e) => {
        const wrongSize =
          e.scrollWidth > e.clientWidth || e.scrollHeight > e.clientHeight
        if (wrongSize) {
          if (actualSize === ChyronSize.Small) {
            setActualSize(ChyronSize.Medium)
          } else if (actualSize === ChyronSize.Medium) {
            setActualSize(ChyronSize.Large)
          } else if (actualSize === ChyronSize.Large) {
            setActualSize(ChyronSize.Fullscreen)
          }
        }
        return wrongSize
      })
    }
  }, [size, children, actualSize])

  return (
    <div
      className="chyron"
      data-size={actualSize}
      data-kicker-position={kickerPosition}
      ref={chyronRef}
    >
      <div
        className="chyron-kicker"
        style={{
          '--accent-color': webinarColors.accent,
          '--text-color': webinarColors.accent.contrast,
        }}
      >
        {kickerPosition === 'top' && <span>{kicker}</span>}
      </div>
      <div className="chyron-content">{children}</div>
    </div>
  )
}

export default Chyron
